const CHUNK_LOAD_ERROR_RELOAD_KEY = "properdiet:chunk-load-error-reload";
const CHUNK_LOAD_ERROR_RELOAD_DELAY_MS = 100;
const RECENT_RELOAD_WINDOW_MS = 10_000;

const CHUNK_LOAD_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /ChunkLoadError/i,
  /Loading chunk \S+ failed/i,
  /Unable to preload CSS/i,
];

interface ReloadState {
  path: string;
  timestamp: number;
}

interface ChunkLoadRecoveryOptions {
  now?: number;
  path?: string;
  reload?: () => void;
  scheduleReload?: (callback: () => void, delayMs: number) => unknown;
  storage?: Storage;
}

interface ChunkLoadEventTarget {
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

type VitePreloadErrorEvent = Event & { payload?: unknown };

function getErrorText(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }

  return String(error ?? "");
}

function isReloadState(value: unknown): value is ReloadState {
  return (
    typeof value === "object" &&
    value !== null &&
    "path" in value &&
    "timestamp" in value &&
    typeof value.path === "string" &&
    typeof value.timestamp === "number"
  );
}

function readReloadState(storage: Storage): ReloadState | null {
  try {
    const storedValue = storage.getItem(CHUNK_LOAD_ERROR_RELOAD_KEY);
    if (!storedValue) {
      return null;
    }

    const parsedValue: unknown = JSON.parse(storedValue);
    return isReloadState(parsedValue) ? parsedValue : null;
  } catch {
    return null;
  }
}

function rememberReloadAttempt(storage: Storage, state: ReloadState): void {
  try {
    storage.setItem(CHUNK_LOAD_ERROR_RELOAD_KEY, JSON.stringify(state));
  } catch {
    // If storage is blocked, still allow a single immediate recovery attempt.
  }
}

function getCurrentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

export function isChunkLoadError(error: unknown): boolean {
  const errorText = getErrorText(error);
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(errorText));
}

export function shouldReloadForChunkLoadError(
  error: unknown,
  options: ChunkLoadRecoveryOptions = {},
): boolean {
  if (!isChunkLoadError(error)) {
    return false;
  }

  const now = options.now ?? Date.now();
  const path = options.path ?? getCurrentPath();
  const storage = options.storage ?? window.sessionStorage;
  const previousAttempt = readReloadState(storage);

  if (
    previousAttempt?.path === path &&
    now - previousAttempt.timestamp < RECENT_RELOAD_WINDOW_MS
  ) {
    return false;
  }

  rememberReloadAttempt(storage, { path, timestamp: now });
  return true;
}

export function reloadForChunkLoadError(
  error: unknown,
  options: ChunkLoadRecoveryOptions = {},
): boolean {
  if (!shouldReloadForChunkLoadError(error, options)) {
    return false;
  }

  const reload = options.reload ?? (() => window.location.reload());
  const scheduleReload =
    options.scheduleReload ??
    ((callback: () => void, delayMs: number) =>
      window.setTimeout(callback, delayMs));

  scheduleReload(reload, CHUNK_LOAD_ERROR_RELOAD_DELAY_MS);
  return true;
}

export function registerChunkLoadErrorHandler(
  target: ChunkLoadEventTarget = window,
  options: ChunkLoadRecoveryOptions = {},
): () => void {
  const handlePreloadError: EventListener = (event) => {
    const preloadErrorEvent = event as VitePreloadErrorEvent;

    if (reloadForChunkLoadError(preloadErrorEvent.payload ?? event, options)) {
      event.preventDefault();
    }
  };

  target.addEventListener("vite:preloadError", handlePreloadError);
  return () => target.removeEventListener("vite:preloadError", handlePreloadError);
}
